/**
 * Escolha da imagem certa para o tamanho que a tela desenha.
 *
 * A API passou a expor variantes ao lado do original (`logo_thumb`,
 * `logo_card`, `team_logo_thumb`), e o original continua no mesmo campo de
 * antes — de propósito, para não quebrar o app mobile em produção.
 *
 * O fallback aqui não é zelo: a API de **produção** não tem variante nenhuma,
 * e o web fala com ela em `app.footie.com.br`. Sem o `||`, todo escudo sumiria
 * lá no dia em que este código subisse.
 */

/**
 * Escudo de time, no tamanho que a tela desenha.
 *
 * `thumb` (limite 128 px) para avatar — grupos, partidas, busca, faixa do
 * campeão. `card` (limite 960 px) para a grade de equipes, que desenha o
 * escudo num bloco de 160 px de altura por até ~460 px de largura.
 *
 * O padrão continua sendo `thumb` porque a maioria dos usos é avatar; quem
 * desenha grande precisa PEDIR. Servir `thumb` num bloco grande o amplia 3,6×
 * — 7× em tela DPR 2 — e trocar peso por borrão não é otimizar.
 */
export function escudo(obj, tamanho = "thumb") {
  if (!obj) return null;

  if (tamanho === "card") {
    // A cadeia inteira degrada: card -> thumb -> original. A API de PRODUÇÃO
    // não tem variante nenhuma, e o web fala com ela.
    return (
      obj.team_logo_card || obj.logo_card ||
      obj.team_logo_thumb || obj.logo_thumb ||
      obj.team_logo || obj.logo || null
    );
  }

  return obj.team_logo_thumb || obj.team_logo || obj.logo_thumb || obj.logo || null;
}

/**
 * Logo de campeonato: `card` para a listagem, `thumb` para menções pequenas.
 *
 * A cadeia inteira degrada, nos dois sentidos: `thumb -> card -> logo` e
 * `card -> thumb -> logo`. Campeonato antigo pode ter só o original (imagem
 * nunca processada) ou só uma das variantes — e o escudo não pode sumir por
 * isso.
 */
export function logoDeCampeonato(atributos, tamanho = "card") {
  if (!atributos) return null;
  if (tamanho === "thumb") {
    return atributos.logo_thumb || atributos.logo_card || atributos.logo || null;
  }
  return atributos.logo_card || atributos.logo_thumb || atributos.logo || null;
}

/**
 * O campeão de um campeonato, quando existe.
 *
 * O dado SEMPRE esteve no payload — `WinnerSerializer` entrega posição, nome e
 * escudo, e o `EditionSerializer` os embute ordenados. O web lia isso apenas
 * para decidir se mostrava o selo "Finalizado" e **descartava nome e escudo**.
 * Por isso o EP09-02 é interface, não API.
 *
 * Procura a posição `1st` em vez de pegar o primeiro da lista: a ordenação vem
 * do servidor (`order(:position)`), mas confiar nela para decidir QUEM é o
 * campeão significaria mostrar o vice como campeão no dia em que um campeonato
 * tiver 2º e 3º registrados sem o 1º.
 */
export function campeao(atributos) {
  const vencedores =
    atributos?.edition?.data?.[0]?.attributes?.winners?.data || [];

  const primeiro = vencedores.find((v) => v?.attributes?.position === "1st");
  if (!primeiro) return null;

  return {
    nome: primeiro.attributes.champ_team_name,
    // `escudo()` já degrada para o original: a API de PRODUÇÃO não tem
    // variante nenhuma, e o web fala com ela.
    escudo: escudo(primeiro.attributes)
  };
}
